import { checkRegistrationStatus } from 'modules/registration/actions';
import { totalPrice } from 'modules/registration/selectors/prices/totalPrice';
import { registrationAnalytics } from 'modules/registration/analytics';
import { AnalyticsRegistrationEvents } from 'modules/registration/utils';

import { changeDistanceService } from 'src/modules/changeDistance/services';

import { authorizedRace } from 'stores';
import registrationStatus from 'stores/registrationStatus';

import { registrationService } from '../../services';

import {
  changeDistanceStore,
  isPaymentSuccessfullyCompleted,
  isPaymentSucceeded,
  isPaymentFrameOpened,
  isPayOnSiteSelectedStore,
  isPayWithEpassiSelectedStore,
  turnstileStore,
} from '../../stores';

import { mainConditions } from '../../selectors/mainConditions';
import { distanceSelector } from '../../selectors/mappedData/distance';
import { raceSelector } from '../../selectors/mappedData/race';
import { constructGroupRegistrationsRequest } from '../../selectors/requests';
import { errorsActions } from '../errors';
import { loaderActions } from '../loader';
import { processPayment } from '../processPayment';
import { snackActions } from '../snacks';
import { executeProfileActions } from './profileActions';

async function submitGroupRegistrations() {
  if (!(await executeProfileActions())) {
    return;
  }

  const request = constructGroupRegistrationsRequest();
  const distanceId = distanceSelector.id.get();
  const raceId = raceSelector.id.get();

  if (!distanceId || !raceId) {
    return;
  }

  /**
   * Token required to register into the secret race
   * is_secret:true
   */
  let registrationToken: string | nil;

  if (mainConditions.isSecretRace.get() && raceId) {
    registrationToken = authorizedRace.findToken(raceId);
  }

  const changeDistanceToken = changeDistanceStore.token;

  if (changeDistanceToken) {
    const quote = await changeDistanceService.loadDetails(changeDistanceToken);

    if (!quote.ok) {
      changeDistanceStore.setError(quote.code);
      return;
    }

    changeDistanceStore.setDetails(quote.details);
  }

  const response = changeDistanceToken
    ? await registrationService.submitChangeDistance(raceId, { ...request, change_distance_token: changeDistanceToken })
    : await registrationService.submitSingleGroup(raceId, registrationToken, request);

  if (response.isOk) {
    if (isPayWithEpassiSelectedStore.value) {
      // Redirect to epassi
      if (response.data?.value) {
        registrationAnalytics.checkoutBegun(response.data?.registration?.uuid);
        registrationAnalytics.leavingPage();
        window.location.href = response.data.value;
        return;
      } else {
        registrationAnalytics.systemError('epassi_url_missing');
        throw new Error('No ePassi URL received from backend');
      }
    }

    if (
      isPayOnSiteSelectedStore.value ||
      isPaymentSucceeded.value === 'singleSucceeded' ||
      (!response.data?.value && totalPrice.get() === 0) ||
      response.data?.payment?.uuid === null
    ) {
      registrationAnalytics.checkoutBegun(response.data?.registration?.uuid);
      await checkRegistrationStatus(response.data?.registration?.uuid || '');
    } else {
      processPayment(
        response.data?.value!,
        response.data?.operations!,
        response.data?.registration?.uuid!,
        response.data?.payment?.uuid!,
        response.data?.service_fee!,
        response.data?.payment_method_order!,
        response.data?.expiration!,
      );
    }
  }

  if (!response.isOk) {
    errorsActions.catchGroupRegistrantsErrors(response.errors);
    errorsActions.catchGroupRegistrationAdditionalFields(response.errors);
    errorsActions.catchGroupRegistrantCustomFieldErrors(response.errors);
    errorsActions.catchRegistrationToastErrors(response.message, response.errors);

    /**
     * A Turnstile token is single-use, so any rejected submit burns it.
     * Reset the widget to issue a fresh challenge for the retry.
     */
    turnstileStore.reset();

    /**
     * reload distance and race to enable snacks error handling, see
     * reactions/observeConditionsForPermanentSnacks.ts
     * TODO, should be replaced with live data streaming
     */
    const reloads = Promise.all([loaderActions.loadDistance(), loaderActions.loadRace()]);
    registrationAnalytics.submitRejected(response.status, response.errors, reloads);
  }
}

const endGroupProcessPayment = () => {
  registrationStatus.isRegisteredSuccessfully = true;
  AnalyticsRegistrationEvents.freeGroupRegistrationSuccessful();
  registrationAnalytics.completed();
  isPaymentSuccessfullyCompleted.on();
  snackActions.payment.successSnack.show({ freeDistance: true });
  isPaymentFrameOpened.off(); // needs to disable here because old logic doesn't work
  isPaymentSucceeded.clear();
};

export { submitGroupRegistrations, endGroupProcessPayment };
